import React, {
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { Dialog } from '@sandtech-hub-monorepo/ui-kit';
import {
  Education,
  GetCheckingCodeDocument,
  GetCurrentCheckInDocument,
  GetCurrentCheckInQuery,
} from '@sandtech-hub-monorepo/ehub-client';
import { QRCodeSVG } from 'qrcode.react';
import { useQuery } from '@apollo/client';
import { CheckInSuccess } from './components/CheckInSuccess';
import { CheckOutSuccess } from './components/CheckOutSuccess';

export const AttendanceDialog = ({
  open,
  handleOpenChange,
}: {
  open: boolean;
  handleOpenChange: (open: boolean) => void;
  education?: Education;
}) => {
  const [prevCheckIn, setPrevCheckIn] =
    useState<GetCurrentCheckInQuery['viewer']['currentCheckIn']>();
  const [successDialog, setSuccessDialog] = useState<ReactElement>();
  const [countdown, setCountdown] = useState(60);

  const {
    data,
    loading: loading2,
    refetch,
  } = useQuery(GetCheckingCodeDocument, { variables: {}, skip: !open });
  const { data: currentCheckIn, loading: loading1 } = useQuery(
    GetCurrentCheckInDocument,
    {
      pollInterval: 5000,
      skip: !open,
    }
  );

  useEffect(() => {
    if (!open) {
      setPrevCheckIn(undefined);
      setSuccessDialog(undefined);
      return;
    }

    if (!currentCheckIn) {
      return;
    }

    if (prevCheckIn === undefined) {
      if (
        currentCheckIn.viewer.currentCheckIn &&
        !currentCheckIn.viewer.currentCheckIn.checkedOutAt
      ) {
        setPrevCheckIn(currentCheckIn.viewer.currentCheckIn);
      } else if (currentCheckIn.viewer.currentCheckIn) {
        setSuccessDialog(
          <CheckOutSuccess
            checkedOutHubDetails={currentCheckIn.viewer.currentCheckIn}
            onClose={() => handleOpenChange(false)}
          />
        );
      } else {
        setPrevCheckIn(null);
      }
    } else {
      // User checked out
      if (prevCheckIn && currentCheckIn.viewer.currentCheckIn?.checkedOutAt) {
        setSuccessDialog(
          <CheckOutSuccess
            checkedOutHubDetails={currentCheckIn.viewer.currentCheckIn}
            onClose={() => handleOpenChange(false)}
          />
        );
      } else if (!prevCheckIn && currentCheckIn.viewer.currentCheckIn) {
        setSuccessDialog(
          <CheckInSuccess
            checkedInHubDetails={currentCheckIn.viewer.currentCheckIn}
            onClose={() => handleOpenChange(false)}
          />
        );
      }
    }
  }, [currentCheckIn, open]);

  const loading = useMemo(() => loading1 || loading2, [loading1, loading2]);

  const onOpenChange = useCallback(
    (open: boolean) => {
      handleOpenChange(open);
    },
    [handleOpenChange]
  );

  useEffect(() => {
    if (open) {
      refetch();
      // TODO maybe always refetch here in case code expires
    }
  }, [open]);

  useEffect(() => {
    if (data) {
      setCountdown(data.viewer.hubCheckInCode?.timeLeft ?? 60);
    }
  }, [data]);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setInterval(() => {
        setCountdown((prevCountdown) => prevCountdown - 1);
      }, 1000);
      return () => clearInterval(timer); // Cleanup the interval on unmount
    } else {
      if (open) {
        refetch();
      }
    }
  }, [countdown]);

  const date = new Date();
  date.setHours(0, 0, 0, 0);

  const durationDate = new Date(
    new Date('01/01/2020').valueOf() +
      new Date().valueOf() -
      new Date(prevCheckIn?.checkedInAt || Date.now()).valueOf()
  );

  return (
    <Dialog className={`bg-card`} open={open} onOpenChange={onOpenChange}>
      {successDialog ? (
        successDialog
      ) : (
        <div className="text-center flex flex-col items-center">
          <div className="flex justify-center mb-4">
            {!loading && (
              <h1
                className={`text-2xl font-semibold ${
                  prevCheckIn ? 'text-destructive' : 'text-success'
                }`}
              >
                {prevCheckIn ? 'Checking you out' : 'Checking you into a hub'}
              </h1>
            )}
          </div>
          {loading && (
            <>
              <div className="flex justify-center text-center mb-4">
                <div className=" animate-pulse w-1/2 h-6 bg-gray-300 rounded-lg"></div>
              </div>
              <div className="flex justify-center mb-4">
                <div className="animate-pulse w-64 h-64 bg-gray-300 rounded-lg"></div>
              </div>
            </>
          )}
          {!loading && (
            <>
              <p className="w-3/5 mx-auto font-light">
                {prevCheckIn
                  ? 'Please scan the QR code above with your phone to check you out.'
                  : 'Please use this QR code to check yourself into your Hub.'}
              </p>
              {prevCheckIn && (
                <>
                  <div className="text-primary font-light text-2xl mt-1">
                    Time spent at this Hub:
                  </div>
                  <div className="text-primary font-semibold text-2xl">
                    {durationDate.getHours()} hrs {durationDate.getMinutes()}{' '}
                    mins
                  </div>
                </>
              )}
              <div className="flex justify-center my-4">
                <QRCodeSVG
                  value={
                    prevCheckIn
                      ? data?.viewer.hubCheckOutCode?.value || ''
                      : data?.viewer.hubCheckInCode?.value || ''
                  }
                  size={256}
                  level="M"
                />
              </div>
              <div className="text-center mb-4">
                New code in: <span className="font-bold">{countdown}</span>{' '}
                seconds
              </div>
            </>
          )}
        </div>
      )}
    </Dialog>
  );
};
